'use client';

import { useMemo, useRef } from 'react';
import type { GameTrackingInfo, GameType } from '@/types/games';
import type { GameViewModel } from '@/types/games/game-view-model';
import type { MlRecommendationTracking } from '@/types/tracking/ml-recommendation';
import type { RotatingTitles } from '@/types/zones/zone';
import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { ZoneHeader } from '@/app/components/zones/subcomponents/zone-header/zone-header';
import { DraggableContainer } from '@/app/elements/draggable-wrapper/draggable-container';
import { ZoneLayoutProvider } from '@/app/components/zones/contexts/zone-layout-context';
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { classes } from '@/utils/classes/classes';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useGameZoneImpression } from '@/hooks/use-game-zone-impression/use-game-zone-impression';
import { MlRecommendationProvider } from '@/app/components/zones/contexts/ml-recommendation-context';

export type GamesGridZoneMeta = {
    title?: string;
    categoryUid?: string;
    rotatingTitles?: RotatingTitles;
    zoneUid?: string;
    zoneType: string;
    zoneLayout?: string;
    bannerId: string;
    bannerName?: string;
    zoneName: string;
    zoneModule?: string;
    automatedZone?: boolean | null;
    gridClassModifier?: string;
    isDynamicOrdering?: boolean;
    forwardTileTracking?: boolean;
};

type GamesGridZoneProps = {
    games: GameViewModel[];
    tracking: GameTrackingInfo[];
    gridLayout: PRISMIC_ZONE_LAYOUT;
    meta: GamesGridZoneMeta;
    isOnlyChildZone?: boolean;
    itemsType?: GameType;
    hideGameInfo?: boolean;
    mlRecommendation?: MlRecommendationTracking | null;
};

export const GamesGridZone = ({
    games,
    tracking,
    gridLayout,
    meta,
    isOnlyChildZone = false,
    itemsType,
    hideGameInfo = false,
    mlRecommendation = null
}: GamesGridZoneProps) => {
    const { isMobile } = useGlobalContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const appliedZoneLayout = COLLECTION_ZONE_LAYOUT[gridLayout];

    const gameElements = useMemo(
        () =>
            games.map((game, index) => {
                const tileTracking = meta.forwardTileTracking
                    ? {
                          gamePosition: index,
                          rankOrder: tracking[index]?.rank_order ?? null,
                          triggerGameName: tracking[index]?.data?.trigger_game_name ?? null,
                          internalCode: tracking[index]?.data?.internal_code ?? null
                      }
                    : { gamePosition: index };

                return (
                    <GameFactoryClient
                        key={game.uid}
                        gameType={itemsType}
                        game={game}
                        index={index + 1}
                        layout={gridLayout}
                        hideGameInfo={hideGameInfo}
                        priority={index === 0}
                        tracking={tileTracking}
                    />
                );
            }),
        [games, itemsType, gridLayout, hideGameInfo, tracking, meta.forwardTileTracking]
    );

    useGameZoneImpression({
        containerRef,
        items: tracking,
        zoneUid: meta.zoneUid,
        zoneConfig: {
            zoneName: meta.zoneName,
            zoneType: meta.zoneType,
            zoneLayout: meta.zoneLayout ?? appliedZoneLayout ?? null,
            automatedZone: meta.automatedZone
        },
        mlRecommendation
    });

    return (
        <MlRecommendationProvider value={mlRecommendation}>
            <ZoneLayoutProvider layout={gridLayout}>
                {!isOnlyChildZone && (
                    <ZoneHeader
                        title={meta.title}
                        categoryUid={meta.categoryUid}
                        rotatingTitles={meta.rotatingTitles}
                        zoneUid={meta.zoneUid}
                        mlRecommendation={mlRecommendation}
                    />
                )}
                <div
                    ref={containerRef}
                    data-t-bannerid={meta.bannerId}
                    data-t-bannername={meta.bannerName ?? meta.zoneName}
                    data-t-zonetype={meta.zoneType}>
                    <DraggableContainer indicators={!isMobile}>
                        <div
                            className={classes('grid-zone-layout', meta.gridClassModifier, appliedZoneLayout)}
                            data-t-zonename={meta.zoneName}
                            data-t-zonemodule={meta.zoneModule ?? meta.bannerId}
                            data-t-zonelayout={meta.zoneLayout ?? appliedZoneLayout}
                            data-t-dynamicordering={meta.isDynamicOrdering ? 'true' : 'false'}>
                            {gameElements}
                        </div>
                    </DraggableContainer>
                </div>
            </ZoneLayoutProvider>
        </MlRecommendationProvider>
    );
};
